import { useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { assets } from '../../config/assets'
import { useAuth } from '../../contexts/AuthContext'

const TWO_LINE_STYLE = {
  display: 'flex',
  flexDirection: 'column' as const,
  alignItems: 'center' as const,
  lineHeight: '1.25',
  gap: '1px',
}

export function Navigation() {
  const { user } = useAuth()
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const location = useLocation()

  const loginLabel = user ? 'My Portal' : 'Member Login'
  const loginTo = user ? '/portal' : '/login'

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 0)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  // Close mobile menu on route change
  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  const navLinks: { to: string; label: string; desktopLabel?: ReactNode; external?: boolean }[] = [
    {
      to: 'https://riderswayapp.com',
      external: true,
      label: "Rider's Way App",
      desktopLabel: (
        <span style={TWO_LINE_STYLE}>
          <span>Rider's Way</span>
          <span>App</span>
        </span>
      ),
    },
    {
      to: '/accelerator',
      label: 'Confidence Accelerator',
      desktopLabel: (
        <span style={TWO_LINE_STYLE}>
          <span>Confidence</span>
          <span>Accelerator</span>
        </span>
      ),
    },
    {
      to: '/horse-sense',
      label: 'Horse Sense Coaching',
      desktopLabel: (
        <span style={TWO_LINE_STYLE}>
          <span>Horse Sense</span>
          <span>Coaching</span>
        </span>
      ),
    },
    { to: '/masterclass', label: 'Masterclasses' },
    { to: '/schedule', label: 'Schedule' },
    { to: '/resources', label: 'Resources' },
    { to: '/store', label: 'Store' },
  ]

  const mobileLinks: { to: string; label: string; external?: boolean }[] = [
    ...navLinks.map(({ to, label, external }) => ({ to, label, external })),
  ]

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 bg-navy transition-shadow duration-200 ${
          scrolled ? 'shadow-lg shadow-black/30' : ''
        }`}
      >
        <nav className="max-w-7xl mx-auto px-4 sm:px-6 xl:px-8">
          <div className="flex items-center justify-between h-20 xl:h-24">
            {/* Logo */}
            <Link
              to="/"
              className="flex-shrink-0"
              onClick={() => setMenuOpen(false)}
            >
              <img
                src={assets.logos.primaryNav}
                alt="Advantage Horsemanship"
                width={454}
                height={72}
                style={{ height: '36px', width: 'auto', display: 'block' }}
              />
            </Link>

            {/* Desktop nav links */}
            <div className="hidden xl:flex items-center gap-4 2xl:gap-8">
              {navLinks.map(({ to, label, desktopLabel, external }) =>
                external ? (
                  <a
                    key={to}
                    href={to}
                    className="font-body text-sm font-medium tracking-wide transition-colors duration-150 min-h-[52px] inline-flex items-center pb-0.5 border-b-2 text-white border-transparent hover:text-gold hover:border-gold"
                  >
                    {desktopLabel ?? label}
                  </a>
                ) : (
                  <NavLink
                    key={to}
                    to={to}
                    className={({ isActive }) =>
                      `font-body text-sm font-medium tracking-wide transition-colors duration-150 min-h-[52px] inline-flex items-center pb-0.5 border-b-2 ${
                        isActive
                          ? 'text-gold border-gold'
                          : 'text-white border-transparent hover:text-gold hover:border-gold'
                      }`
                    }
                  >
                    {desktopLabel ?? label}
                  </NavLink>
                )
              )}
            </div>

            {/* Desktop CTA */}
            <div className="hidden xl:flex items-center">
              <Link
                to={loginTo}
                className="font-body text-sm font-semibold tracking-wide text-gold border border-gold px-5 py-2.5 min-h-[44px] inline-flex items-center transition-colors duration-150 hover:bg-gold/10"
              >
                {loginLabel}
              </Link>
            </div>

            {/* Mobile hamburger */}
            <div className="xl:hidden flex items-center">
              <button
                className="flex flex-col justify-center items-center w-11 h-11 gap-1.5 text-white"
                onClick={() => setMenuOpen((o) => !o)}
                aria-label={menuOpen ? 'Close menu' : 'Open menu'}
                aria-expanded={menuOpen}
              >
                <span
                  className={`block w-6 h-0.5 bg-current transition-transform duration-200 ${
                    menuOpen ? 'rotate-45 translate-y-2' : ''
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 bg-current transition-opacity duration-200 ${
                    menuOpen ? 'opacity-0' : ''
                  }`}
                />
                <span
                  className={`block w-6 h-0.5 bg-current transition-transform duration-200 ${
                    menuOpen ? '-rotate-45 -translate-y-2' : ''
                  }`}
                />
              </button>
            </div>
          </div>
        </nav>
      </header>

      {/* Mobile menu - full screen overlay */}
      {menuOpen && (
        <div className="fixed inset-0 z-[60] bg-navy flex flex-col xl:hidden overflow-hidden">
          {/* Top bar mirrors header */}
          <div className="flex items-center justify-between h-16 px-4">
            <Link to="/" onClick={() => setMenuOpen(false)}>
              <img
                src={assets.logos.primaryNav}
                alt="Advantage Horsemanship"
                width={454}
                height={72}
                style={{ height: '36px', width: 'auto', display: 'block' }}
              />
            </Link>
            <button
              className="flex flex-col justify-center items-center w-11 h-11 gap-1.5 text-white"
              onClick={() => setMenuOpen(false)}
              aria-label="Close menu"
            >
              <span className="block w-6 h-0.5 bg-current rotate-45 translate-y-[3px]" />
              <span className="block w-6 h-0.5 bg-current -rotate-45 -translate-y-[3px]" />
            </button>
          </div>

          {/* Links */}
          <nav className="flex flex-col px-4 pt-8 gap-1 overflow-y-auto flex-1 min-h-0">
            {mobileLinks.map(({ to, label, external }) =>
              external ? (
                <a
                  key={to}
                  href={to}
                  onClick={() => setMenuOpen(false)}
                  className="font-body text-lg font-medium py-4 border-b border-white/10 min-h-[52px] flex items-center transition-colors duration-150 text-white hover:text-gold"
                >
                  {label}
                </a>
              ) : (
                <NavLink
                  key={to}
                  to={to}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) =>
                    `font-body text-lg font-medium py-4 border-b border-white/10 min-h-[52px] flex items-center transition-colors duration-150 ${
                      isActive ? 'text-gold' : 'text-white hover:text-gold'
                    }`
                  }
                >
                  {label}
                </NavLink>
              )
            )}

            {/* Mobile CTA */}
            <div className="pt-8 pb-6 flex-shrink-0">
              <Link
                to={loginTo}
                onClick={() => setMenuOpen(false)}
                className="font-body text-base font-semibold tracking-wide bg-gold text-navy w-full min-h-[52px] flex items-center justify-center"
              >
                {loginLabel}
              </Link>
            </div>
          </nav>
        </div>
      )}

      {/* Spacer to offset fixed header */}
      <div className="h-20 xl:h-24" aria-hidden="true" />
    </>
  )
}
